import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { GripVertical, PencilSquare, Plus, Trash } from 'react-bootstrap-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import PageLayout from '../../layouts/PageLayout';
import { aiProviderAccountAPI } from '../../services';

/**
 * #133517 — Màn "Tài khoản AI" (chỉ ADMIN): quản lý khoá API các nhà cung cấp AI cho trợ lý hướng
 * dẫn game (OpenCode Go nhiều tài khoản + kéo thả thứ tự; Gemini/Groq 1 tài khoản), kèm cấu hình
 * giọng đọc + tốc độ đọc.
 *
 * Khoá LUÔN hiện dạng che 4 ký tự cuối — máy chủ không bao giờ trả khoá thật ở đường đọc.
 */

const PROVIDER_ORDER = ['go', 'gemini', 'groq'];
const PROVIDER_LABEL = {
  go: 'OpenCode Go',
  gemini: 'Google Gemini',
  groq: 'Groq',
};
const PROVIDER_HINT = {
  go: 'Được nhiều tài khoản. Kéo thả để đổi thứ tự gọi — tài khoản trên cùng được gọi trước.',
  gemini: 'Chỉ 1 tài khoản.',
  groq: 'Chỉ 1 tài khoản.',
};

function SortableAccountRow({ account, onEdit, onToggle, onRemove, busy }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: account.id,
  });
  return (
    <div
      ref={setNodeRef}
      className="border rounded p-2 mb-2"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        background: isDragging ? '#eef4ff' : '#fff',
      }}
      data-testid={`tk-ai-${account.id}`}
    >
      <div className="d-flex align-items-center gap-2">
        <span
          {...attributes}
          {...listeners}
          role="button"
          aria-label={`Kéo để đổi thứ tự ${account.label}`}
          title="Kéo để đổi thứ tự"
          className="text-muted flex-shrink-0"
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', padding: '6px 2px' }}
        >
          <GripVertical size={20} />
        </span>
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate">{account.label}</div>
          <small className="text-muted" style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
            {account.api_key_masked}
          </small>
        </div>
        <Badge bg={account.is_active ? 'success' : 'secondary'}>{account.is_active ? 'Bật' : 'Tắt'}</Badge>
      </div>
      <div className="d-flex gap-2 mt-2">
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={busy}
          onClick={() => onToggle(account)}
          data-testid={`nut-bat-tat-${account.id}`}
        >
          {account.is_active ? 'Tắt' : 'Bật'}
        </Button>
        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => onEdit(account)}>
          <PencilSquare size={14} className="me-1" />
          Sửa
        </Button>
        <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => onRemove(account)}>
          <Trash size={14} />
        </Button>
      </div>
    </div>
  );
}

function StaticAccountRow({ account, onEdit, onToggle, busy }) {
  return (
    <div className="border rounded p-2 mb-2" data-testid={`tk-ai-${account.id}`}>
      <div className="d-flex align-items-center gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate">{account.label}</div>
          <small className="text-muted" style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
            {account.api_key_masked}
          </small>
        </div>
        <Badge bg={account.is_active ? 'success' : 'secondary'}>{account.is_active ? 'Bật' : 'Tắt'}</Badge>
      </div>
      <div className="d-flex gap-2 mt-2">
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={busy}
          onClick={() => onToggle(account)}
          data-testid={`nut-bat-tat-${account.id}`}
        >
          {account.is_active ? 'Tắt' : 'Bật'}
        </Button>
        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => onEdit(account)}>
          <PencilSquare size={14} className="me-1" />
          Sửa
        </Button>
      </div>
    </div>
  );
}

export default function AiAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tts, setTts] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formProvider, setFormProvider] = useState('go');
  const [formLabel, setFormLabel] = useState('');
  const [formKey, setFormKey] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = async () => {
    try {
      setLoading(true);
      const [res, ttsRes] = await Promise.all([
        aiProviderAccountAPI.list(),
        aiProviderAccountAPI.getTtsSettings(),
      ]);
      setAccounts(res?.data || res || []);
      setTts(ttsRes || null);
    } catch (e) {
      toast.error(e?.message || 'Không tải được danh sách tài khoản AI');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = (provider) => {
    setEditing(null);
    setFormProvider(provider);
    setFormLabel('');
    setFormKey('');
    setShowModal(true);
  };

  const openEdit = (account) => {
    setEditing(account);
    setFormProvider(account.provider);
    setFormLabel(account.label);
    setFormKey('');
    setShowModal(true);
  };

  const save = async () => {
    try {
      setBusy(true);
      if (editing) {
        const data = { label: formLabel.trim() };
        if (formKey.trim()) data.api_key = formKey.trim();
        await aiProviderAccountAPI.update(editing.id, data);
        toast.success('Đã cập nhật tài khoản AI');
      } else {
        await aiProviderAccountAPI.create({
          provider: formProvider,
          label: formLabel.trim(),
          api_key: formKey.trim(),
        });
        toast.success('Đã thêm tài khoản AI');
      }
      setShowModal(false);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Lưu tài khoản AI thất bại');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (account) => {
    if (!window.confirm(`Xoá tài khoản "${account.label}"?`)) return;
    try {
      setBusy(true);
      await aiProviderAccountAPI.remove(account.id);
      toast.success('Đã xoá tài khoản AI');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Xoá thất bại');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (account) => {
    try {
      setBusy(true);
      await aiProviderAccountAPI.update(account.id, { is_active: !account.is_active });
      await load();
    } catch (e) {
      toast.error(e?.message || 'Đổi trạng thái thất bại');
    } finally {
      setBusy(false);
    }
  };

  const handleDragEnd = async (provider, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const group = accounts.filter((a) => a.provider === provider);
    const oldIndex = group.findIndex((a) => a.id === active.id);
    const newIndex = group.findIndex((a) => a.id === over.id);
    const reordered = arrayMove(group, oldIndex, newIndex);
    const others = accounts.filter((a) => a.provider !== provider);
    // Cập nhật ngay trên màn cho mượt, rồi mới gọi máy chủ.
    setAccounts([...others, ...reordered]);
    try {
      await aiProviderAccountAPI.reorder(
        provider,
        reordered.map((a) => a.id),
      );
      toast.success('Đã lưu thứ tự gọi');
    } catch (e) {
      toast.error(e?.message || 'Lưu thứ tự thất bại');
      await load();
    }
  };

  const saveTts = async () => {
    if (!tts) return;
    try {
      setBusy(true);
      const saved = await aiProviderAccountAPI.saveTtsSettings({
        speaking_rate: Number(tts.speaking_rate),
        voice: tts.voice,
      });
      setTts({ ...tts, ...saved });
      toast.success('Đã lưu cấu hình giọng đọc');
    } catch (e) {
      toast.error(e?.message || 'Lưu cấu hình giọng đọc thất bại');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageLayout>
      <Row>
        <Col xs={12}>
          <div className="mb-3">
            <h3 className="mb-1">Tài khoản AI</h3>
            <p className="text-muted mb-0">
              Khoá API cho trợ lý hướng dẫn game. Khoá luôn hiện dạng che, chỉ 4 ký tự cuối.
            </p>
          </div>
        </Col>

        {loading ? (
          <Col xs={12} className="text-center py-5">
            <Spinner animation="border" />
          </Col>
        ) : (
          <>
            {PROVIDER_ORDER.map((provider) => {
              const group = accounts
                .filter((a) => a.provider === provider)
                .sort((a, b) => a.priority - b.priority);
              const canAdd = provider === 'go' || group.length === 0;
              return (
                <Col xs={12} key={provider} className="mb-3">
                  <Card data-testid={`nhom-${provider}`}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div>
                          <div className="fw-bold">{PROVIDER_LABEL[provider] || provider}</div>
                          <small className="text-muted">{PROVIDER_HINT[provider]}</small>
                        </div>
                        {canAdd && (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => openCreate(provider)}
                            data-testid={`nut-them-${provider}`}
                          >
                            <Plus size={16} className="me-1" />
                            {provider === 'go' ? 'Thêm tài khoản' : 'Đặt khoá'}
                          </Button>
                        )}
                      </div>
                      {group.length === 0 ? (
                        <div className="text-muted small py-2">Chưa có tài khoản nào.</div>
                      ) : provider === 'go' ? (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(e) => handleDragEnd(provider, e)}
                        >
                          <SortableContext
                            items={group.map((a) => a.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {group.map((account) => (
                              <SortableAccountRow
                                key={account.id}
                                account={account}
                                busy={busy}
                                onEdit={openEdit}
                                onToggle={toggle}
                                onRemove={remove}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      ) : (
                        group.map((account) => (
                          <StaticAccountRow
                            key={account.id}
                            account={account}
                            busy={busy}
                            onEdit={openEdit}
                            onToggle={toggle}
                          />
                        ))
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}

            <Col xs={12} className="mb-4">
              <Card data-testid="cau-hinh-giong-doc">
                <Card.Body>
                  <div className="fw-bold mb-1">Cấu hình giọng đọc</div>
                  <small className="text-muted d-block mb-3">
                    Áp cho các câu trả lời sinh MỚI sau khi lưu; audio đã đọc sẵn từ trước vẫn phát theo
                    giọng/tốc độ cũ tới khi hết đệm.
                  </small>
                  {tts && (
                    <Alert variant="info" className="py-2 small">
                      Giọng mặc định: <b>Nam Trầm Ấm</b> ({tts.default_voice}).
                    </Alert>
                  )}
                  {tts && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Giọng đọc</Form.Label>
                        <Form.Select
                          value={tts.voice}
                          disabled={busy}
                          onChange={(e) => setTts({ ...tts, voice: e.target.value })}
                          style={{ fontSize: 16 }}
                          data-testid="chon-giong-doc"
                        >
                          {(tts.voices || []).map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name} ({v.gender === 'nam' ? 'nam' : 'nữ'})
                            </option>
                          ))}
                          {!(tts.voices || []).some((v) => v.id === tts.voice) && (
                            <option value={tts.voice}>{tts.voice}</option>
                          )}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Tốc độ đọc: <b>{Number(tts.speaking_rate).toFixed(2)}</b>
                        </Form.Label>
                        <Form.Range
                          min={tts.rate_min}
                          max={tts.rate_max}
                          step={0.05}
                          value={tts.speaking_rate}
                          disabled={busy}
                          onChange={(e) => setTts({ ...tts, speaking_rate: Number(e.target.value) })}
                          style={{ fontSize: 16 }}
                          data-testid="thanh-toc-do-doc"
                        />
                        <Form.Text className="text-muted">
                          Từ {tts.rate_min} đến {tts.rate_max}. Mặc định {tts.default_rate}.
                        </Form.Text>
                      </Form.Group>
                      <Button variant="primary" onClick={saveTts} disabled={busy} data-testid="nut-luu-giong-doc">
                        Lưu cấu hình giọng đọc
                      </Button>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </>
        )}
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? `Sửa tài khoản ${editing.label}` : `Thêm tài khoản AI`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {!editing && (
              <Form.Group className="mb-3">
                <Form.Label>Nhà cung cấp</Form.Label>
                <Form.Control value={PROVIDER_LABEL[formProvider] || formProvider} disabled style={{ fontSize: 16 }} />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>
                Tên gợi nhớ <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formLabel}
                disabled={busy}
                onChange={(e) => setFormLabel(e.target.value)}
                placeholder="vd: go1, go2"
                style={{ fontSize: 16 }}
                data-testid="nhap-ten-goi-nho"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {editing ? 'Khoá API mới (bỏ trống nếu không đổi)' : 'Khoá API'}{' '}
                {!editing && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="password"
                autoComplete="new-password"
                value={formKey}
                disabled={busy}
                onChange={(e) => setFormKey(e.target.value)}
                placeholder="Dán khoá API vào đây"
                style={{ fontSize: 16 }}
                data-testid="nhap-khoa-api"
              />
              <Form.Text className="text-muted">
                Khoá không hiện lại sau khi lưu — chỉ hiện 4 ký tự cuối.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={busy}>
            Huỷ
          </Button>
          <Button
            variant="primary"
            onClick={save}
            disabled={busy || !formLabel.trim() || (!editing && !formKey.trim())}
            data-testid="nut-luu-tai-khoan-ai"
          >
            {busy ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Lưu
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}
